<template>
    <div class="ai-faq-widget">
        <div class="ai-faq-content">



                <!-- Rate Limit Indicator -->
                <rate-limit-indicator
                    :remaining="rateLimitRemaining"
                    :show="showMetadata"
                    class="mb-3"
                />

                <!-- Question Input Form -->
                <question-input
                    v-model="currentQuestion"
                    :is-loading="isLoading"
                    :character-limit="characterLimit"
                    :error="inputError"
                    @submit="submitQuestion"
                    @input="clearInputError"
                />

                <!-- Loading Spinner -->
                <loading-spinner v-if="isLoading" class="my-4" />

                <!-- Error Display -->
                <error-message
                    v-if="errorMessage"
                    :error="errorMessage"
                    :error-type="errorType"
                    @retry="retryLastQuestion"
                    @clear="clearError"
                    class="mb-3"
                />

                <!-- Responses History -->
                <div v-if="responses.length > 0" class="responses-history">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h6 class="mb-0">Recent Questions</h6>
                        <span class="cursor" @click="clearHistory">
                            <i class="fas fa-times"></i>
                            Clear History
                        </span>
                    </div>

                    <response-display
                        v-for="(response, index) in reversedResponses"
                        :key="response.id"
                        :response="response"
                        :show-metadata="showMetadata"
                        :is-latest="index === 0"
                        @copy="copyToClipboard"
                        class="mb-3"
                    />
                </div>
            </div>

        </div>
</template>

<script>
import QuestionInput from './components/QuestionInput.vue'
import ResponseDisplay from './components/ResponseDisplay.vue'
import LoadingSpinner from './components/LoadingSpinner.vue'
import ErrorMessage from './components/ErrorMessage.vue'
import RateLimitIndicator from './components/RateLimitIndicator.vue'
import aiFaqApi from '../mixins/aiFaqApi.js'

export default {
    name: 'FaqAgent',

    components: {
        QuestionInput,
        ResponseDisplay,
        LoadingSpinner,
        ErrorMessage,
        RateLimitIndicator
    },

    mixins: [aiFaqApi],

    props: {
        occasionId: {
            type: [Number, String],
            required: true
        },
        apiEndpoint: {
            type: String,
            default: '/api/v0/ai/ask-question'
        },
        showMetadata: {
            type: Boolean,
            default: false
        },
        maxQuestions: {
            type: Number,
            default: 5
        },
        characterLimit: {
            type: Number,
            default: 500
        },
        participantId: {
            type: [Number, String],
            default: null
        }
    },

    data() {
        return {
            currentQuestion: '',
            responses: [],
            isLoading: false,
            errorMessage: '',
            errorType: null,
            inputError: '',
            rateLimitRemaining: 10,
            lastQuestion: ''
        }
    },

    computed: {
        reversedResponses() {
            return [...this.responses].reverse()
        }
    },

    methods: {
        async submitQuestion() {
            // Validate input
            if (!this.validateQuestion()) {
                return
            }
            this.clearError()
            this.clearInputError()
            this.isLoading = true
            this.lastQuestion = this.currentQuestion.trim()

            // Emit event
            this.$emit('question-submitted', {
                question: this.lastQuestion,
                occasionId: this.occasionId,
                participantId: this.participantId,
            })

            try {
                const response = await this.askQuestion(this.lastQuestion, this.occasionId, this.participantId)
                this.handleSuccessResponse(response)
            } catch (error) {
                this.handleErrorResponse(error)
            } finally {
                this.isLoading = false
            }
        },

        validateQuestion() {
            const question = this.currentQuestion.trim()

            if (!question) {
                this.inputError = 'Please enter a question'
                return false
            }

            if (question.length < 5) {
                this.inputError = 'Question must be at least 10 characters long'
                return false
            }

            if (question.length > this.characterLimit) {
                this.inputError = `Question must be no more than ${this.characterLimit} characters long`
                return false
            }

            // Basic HTML/script detection
            if (/<[^>]*>/.test(question)) {
                this.inputError = 'HTML tags are not allowed'
                return false
            }

            return true
        },

        handleSuccessResponse(apiResponse) {
            const response = {
                id: Date.now(),
                question: this.lastQuestion,
                answer: apiResponse.answer,
                sources: apiResponse.sources || [],
                cached: apiResponse.cached || false,
                tokensUsed: apiResponse.tokens_used,
                model: apiResponse.model,
                timestamp: new Date(),
                rateLimitRemaining: apiResponse.rate_limit_remaining
            }

            // Update rate limit
            if (typeof apiResponse.rate_limit_remaining === 'number') {
                this.rateLimitRemaining = apiResponse.rate_limit_remaining
            }

            // Add to responses and limit history
            this.responses.push(response)
            if (this.responses.length > this.maxQuestions) {
                this.responses = this.responses.slice(-this.maxQuestions)
            }

            // Clear current question
            this.currentQuestion = ''

            // Emit event
            this.$emit('response-received', response)
        },

        handleErrorResponse(error) {
            console.error('AI FAQ Error:', error)

            let errorMessage = 'An unexpected error occurred. Please try again.'
            let errorType = 'general'

            if (error.response) {
                const status = error.response.status
                const data = error.response.data

                if (status === 429) {
                    errorType = 'rate-limit'
                    errorMessage = 'Too many requests. Please wait a moment before asking another question.'
                    this.rateLimitRemaining = 0
                    this.$emit('rate-limit-exceeded', { error: data })
                } else if (status === 400 && data.error) {
                    errorType = 'validation'
                    errorMessage = data.error
                } else if (status === 404) {
                    errorType = 'not-found'
                    errorMessage = 'Event not found. Please check the event details.'
                } else if (status >= 500) {
                    errorType = 'server'
                    errorMessage = 'Server error. Our team has been notified. Please try again later.'
                }
            } else if (error.code === 'NETWORK_ERROR') {
                errorType = 'network'
                errorMessage = 'Network error. Please check your connection and try again.'
            }

            this.errorMessage = errorMessage
            this.errorType = errorType

            this.$emit('error-occurred', {
                error: error,
                message: errorMessage,
                type: errorType
            })
        },

        retryLastQuestion() {
            if (this.lastQuestion) {
                this.currentQuestion = this.lastQuestion
                this.clearError()
                this.submitQuestion()
            }
        },

        clearError() {
            this.errorMessage = ''
            this.errorType = null
        },

        clearInputError() {
            this.inputError = ''
        },

        clearHistory() {
            this.responses = []
            this.$emit('history-cleared')
        },

        copyToClipboard(text) {
            // Try modern clipboard API first
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$emit('text-copied', text)
                }).catch(err => {
                    console.error('Clipboard API failed:', err)
                    this.fallbackCopy(text)
                })
            } else {
                this.fallbackCopy(text)
            }
        },

        fallbackCopy(text) {
            const textArea = document.createElement('textarea')
            textArea.value = text
            textArea.style.position = 'fixed'
            textArea.style.left = '-999999px'
            textArea.style.top = '-999999px'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()

            try {
                const successful = document.execCommand('copy')
                if (successful) {
                    this.$emit('text-copied', text)
                } else {
                    console.error('Fallback copy failed')
                }
            } catch (err) {
                console.error('Fallback copy error:', err)
            } finally {
                document.body.removeChild(textArea)
            }
        }
    }
}
</script>

<style scoped>
.ai-faq-widget {
    max-width: 100%;
}

.ai-faq-content {
    /* Normal content styling */
}

.card-title {
    color: #495057;
}

.card-title i {
    color: #007bff;
    margin-right: 0.5rem;
}

.responses-history {
    border-top: 1px solid #dee2e6;
    padding-top: 1rem;
}

@media (max-width: 768px) {
    .card {
        border-left: none;
        border-right: none;
        border-radius: 0;
    }
}

/* Clear History Cursor */
.cursor {
    cursor: pointer;
    color: #6c757d;
    font-size: 0.875rem;
}

.cursor:hover {
    color: #495057;
}

.cursor i {
    margin-right: 0.25rem;
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/AiAgents/FaqAgent/FaqAgent.vue